import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiClock, FiFileText, FiChevronRight, FiInbox } from 'react-icons/fi';
import '../../../styles/Student.css';
import '../assessment-module-mobile.css';
import testService from '../../../services/testService';
import VideoModal from './VideoModal';
import { useAssessmentTooltips } from '../AssessmentContext';
import Layout from '../../../components/Layout/Layout';
import { useUser } from '../../../contexts/UserContext';

const formatTime = (timeStr) => {
  if (!timeStr) return '—';
  try {
    let hours = 0;
    let minutes = 0;
    const cleaned = timeStr.trim().toUpperCase();
    if (cleaned.includes('AM') || cleaned.includes('PM')) {
      const ampmPart = cleaned.includes('PM') ? 'PM' : 'AM';
      const digitPart = cleaned.replace(/[A-Z]/g, '').trim();
      const parts = digitPart.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1] || '0', 10);
      if (ampmPart === 'PM' && hours < 12) hours += 12;
      if (ampmPart === 'AM' && hours === 12) hours = 0;
    } else {
      const parts = cleaned.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1] || '0', 10);
    }
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours % 12;
    displayHour = displayHour ? displayHour : 12;
    const hPad = String(displayHour).padStart(2, '0');
    const mPad = String(minutes).padStart(2, '0');
    return `${hPad}:${mPad} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const MemberAssessmentDashboard = ({ withShell = false }) => {
  useAssessmentTooltips();
  const { user } = useUser();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  const show_my_tests_btn = user && Number(user.user_id || user.id) === 121;
  
  // Video Modal State
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedTestForVideo, setSelectedTestForVideo] = useState(null);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const res = await testService.getStudentAssessments();
      
      if (res.status === 'success') {
        const processedTests = res.assessments.map(pack => {
          const now = new Date();
          const scheduledDate = pack.scheduled_date;
          const startTimeStr = pack.start_time;
          const endTimeStr = pack.end_time;
          const startDateTime = new Date(`${scheduledDate}T${startTimeStr}`);
          const endDateTime = new Date(`${scheduledDate}T${endTimeStr}`);

          const hasSubmitted = pack.submission_status === 'COMPLETED';

          let status = 'UPCOMING';
          let badgeClass = 'status-upcoming';
          
          if (hasSubmitted) {
            status = 'COMPLETED';
            badgeClass = 'status-completed';
          } else if (now >= startDateTime && now <= endDateTime) {
            status = 'ACTIVE';
            badgeClass = 'status-live';
          } else if (now > endDateTime) {
            status = 'EXPIRED';
            badgeClass = 'status-upcoming';
          } else {
            status = 'UPCOMING';
            badgeClass = 'status-upcoming';
          }

          return {
            id: pack.assessment_id,
            packId: pack.id,
            testName: pack.assessment_name,
            batchName: pack.pack_name,
            status,
            badgeClass,
            date: scheduledDate,
            startTime: startTimeStr,
            endTime: endTimeStr,
            add_video: pack.add_video === 'Yes',
            intro_videos: pack.intro_videos ? (typeof pack.intro_videos === 'string' ? JSON.parse(pack.intro_videos) : pack.intro_videos) : []
          };
        });

        let currentUserId = null;
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const decoded = JSON.parse(jsonPayload);
            currentUserId = decoded.data?.id || decoded.user_id || decoded.id;
          }
        } catch (e) {
          console.error("Error decoding token for candidate filtering:", e);
        }

        // Load and merge local published assessments
        const dbPackIds = new Set(processedTests.map(p => String(p.packId)));
        const pubStr = localStorage.getItem('published_tests');
        if (pubStr) {
          try {
            const parsed = JSON.parse(pubStr);
            parsed.forEach(test => {
              (test.groups || []).forEach(group => {
                if (group.status === 'Published') {
                  if (group.backendPackId && dbPackIds.has(String(group.backendPackId))) {
                    return; // Already synced and returned from the database
                  }
                  if (dbPackIds.has(String(group.id))) {
                    return; // Already synced and returned from the database
                  }
                  if (currentUserId && group.candidates) {
                    const candidatesList = String(group.candidates).split(',').map(c => c.trim()).filter(Boolean);
                    if (!candidatesList.includes(String(currentUserId))) {
                      return; // Skip this group as candidate is not assigned
                    }
                  }
                  const now = new Date();
                  const scheduledDate = group.scheduled_date;
                  const startTimeStr = group.start_time;
                  const endTimeStr = group.end_time;
                  const startDateTime = new Date(`${scheduledDate}T${startTimeStr}`);
                  const endDateTime = new Date(`${scheduledDate}T${endTimeStr}`);
                  
                  let status = 'UPCOMING';
                  let badgeClass = 'status-upcoming';
                  
                  const submissionKey = `sub-completed-${test.id}-${group.id}`;
                  const hasSubmitted = localStorage.getItem(submissionKey) === 'COMPLETED';

                  if (hasSubmitted) {
                    status = 'COMPLETED';
                    badgeClass = 'status-completed';
                  } else if (now >= startDateTime && now <= endDateTime) {
                    status = 'ACTIVE';
                    badgeClass = 'status-live';
                  } else if (now > endDateTime) {
                    status = 'EXPIRED';
                    badgeClass = 'status-upcoming';
                  } else {
                    status = 'UPCOMING';
                    badgeClass = 'status-upcoming';
                  }
                  
                  processedTests.push({
                    id: test.id,
                    packId: group.id,
                    testName: test.name,
                    batchName: group.pack_name,
                    status,
                    badgeClass,
                    date: scheduledDate,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                    // Video — read DB-normalized field first, fall back to wizard field
                    add_video: test.add_video === 'Yes' || test.add_video === true || test.videoEnabled === true,
                    intro_videos: Array.isArray(test.intro_videos) ? test.intro_videos
                      : (Array.isArray(test.videoFiles) ? test.videoFiles : []),
                    // Exam config — pass through so TestExecutionPage gets correct flags
                    allow_backtracking: test.allow_backtracking ?? (test.navigationControl ? 1 : 0),
                    proctored_exam:     test.proctored_exam     ?? (test.fullscreenMode    ? 1 : 0),
                    browser_lockdown:   test.browser_lockdown   ?? (test.tabSwitchWarning  ? 1 : 0),
                    show_results:       test.show_results       ?? (test.autoSubmit        ? 1 : 0),
                    isLocal: true
                  });
                }
              });
            });
          } catch (e) {
            console.error("Failed to parse local published tests", e);
          }
        }

        setTests(processedTests.sort((a, b) => {
          const aVal = typeof a.packId === 'number' || !isNaN(a.packId) ? parseInt(a.packId) : 0;
          const bVal = typeof b.packId === 'number' || !isNaN(b.packId) ? parseInt(b.packId) : 0;
          return bVal - aVal;
        }));
      }
    } catch (err) {
      console.error("Failed to fetch student assessments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTakeTest = (test) => {
    if (test.add_video && test.intro_videos && test.intro_videos.length > 0) {
      setSelectedTestForVideo(test);
      setIsVideoModalOpen(true);
    } else {
      navigate(`/execution/${test.id}/${test.packId}`);
    }
  };

  const startExam = () => {
    if (selectedTestForVideo) {
      sessionStorage.setItem(`video_watched_${selectedTestForVideo.id}`, 'true');
      navigate(`/execution/${selectedTestForVideo.id}/${selectedTestForVideo.packId}`);
      setIsVideoModalOpen(false);
    }
  };

  const page = (
    <div className="assess-mod-page assess-mod-student-page student-dashboard">
      <div className="assess-mod-card card-custom">
        <div className="assess-mod-student-header dashboard-header flex items-center justify-between gap-4">
          <div>
            <h2 className="header-title">Scheduled & Live Tests</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Manage and track your assigned test modules</p>
          </div>
          {show_my_tests_btn && (
            <button
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition-all shadow-md hover:translate-y-[-1px] active:translate-y-0 cursor-pointer border-0 outline-none flex-shrink-0"
              onClick={() => {
                localStorage.setItem('assessment_view_as_student', 'false');
                window.dispatchEvent(new Event('assessment_view_mode_change'));
              }}
            >
              <FiArrowLeft />
              <span>Back to Admin Panel</span>
            </button>
          )}
        </div>
        
          {loading ? (
            <div className="empty-state-container">
              <div className="loading-spinner"></div>
              <p>Loading Tests...</p>
            </div>
          ) : tests.length > 0 ? (
            <div className="assess-mod-student-table-wrap assess-mod-table-wrap">
            <table className="assess-mod-student-table modern-table">
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tests.map((test, index) => (
                  <tr key={index}>
                    <td>
                      <div className="test-info-wrapper">
                        <div className="test-icon-box">
                          <FiFileText />
                        </div>
                        <div>
                          <div className="test-name-main">{test.testName}</div>
                          <div className="test-batch-sub">{test.batchName}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${test.badgeClass}`}>{test.status}</span>
                    </td>
                    <td>
                      <div className="schedule-item">
                          <div className="schedule-date">
                            <FiCalendar size={14} style={{ color: '#94a3b8' }} />
                            {new Date(test.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}
                          </div>
                          <div className="time-badge-container">
                            <div className="time-card">{formatTime(test.startTime)}</div>
                            <span className="time-sep">—</span>
                            <div className="time-card">{formatTime(test.endTime)}</div>
                          </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {test.status === 'COMPLETED' ? (
                        <div className="action-text" style={{ color: '#10b981', fontWeight: 800 }}>SUBMITTED</div>
                      ) : test.status === 'ACTIVE' ? (
                        <button 
                          className="take-test-btn" 
                          onClick={() => handleTakeTest(test)}
                        >
                          Take Test
                        </button>
                      ) : test.status === 'EXPIRED' ? (
                        <div className="action-text">CLOSED</div>
                      ) : (
                        <div className="action-text">WAITING</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="empty-state-container">
              <div className="empty-icon-box">
                <FiInbox size={48} />
              </div>
              <h3 className="empty-title">No Tests Assigned</h3>
              <p className="empty-subtitle">You don't have any tests scheduled at the moment.</p>
            </div>
          )}
      </div>

      {selectedTestForVideo && (
        <VideoModal 
          isOpen={isVideoModalOpen}
          videos={selectedTestForVideo.intro_videos}
          testName={selectedTestForVideo.testName}
          onComplete={startExam}
        />
      )}
    </div>
  );

  if (withShell) {
    return <Layout noSidebar>{page}</Layout>;
  }

  return page;
};

export default MemberAssessmentDashboard;
